// === 시즌 스타일 런타임 설정 ===========================================
// 시즌 스타일(할로윈·크리스마스 등)이 언제 목록에 뜰지를 코드 상수가 아니라
// Firestore `config/seasons` 문서에서 읽는다. functions/model-config.js 와
// 같은 취지다 — 창을 바꾸는 데 재배포가 필요 없도록.
//
// 날짜에 따른 자동 전환 자체는 원래도 재배포가 필요 없었다 (판정은 기기
// 시각으로 런타임에 한다). 재배포가 필요했던 건 **창을 바꾸거나 시즌을
// 추가·중단**할 때이고, 네이티브 앱은 그게 스토어 심사까지 간다. 설날처럼
// 해마다 날짜가 움직이는 시즌은 고정 창으로는 계속 어긋난다.
//
//   config/seasons: {
//     halloween:      { from: '09-15', to: '11-02', enabled: true },
//     christmas:      { from: '11-20', to: '12-31', enabled: true },
//     lunar_new_year: { from: '01-29', to: '02-12', enabled: true },
//     spring_blossom: { from: '03-01', to: '05-10', enabled: true },
//     showAll: false   // true 면 날짜 무시하고 전부 노출 (미리보기용)
//   }
//
// 문서가 없거나 필드가 빠지면 ai-service.js 의 style.season 기본값으로
// 동작하므로 이 문서가 장애 지점이 되지 않는다. 규칙에서 config/seasons 만
// 공개 읽기이고 config/ai (모델명) 는 그대로 막혀 있다.
//
// 바꾸는 법: Firebase 콘솔에서 config/seasons 문서 수정 → 아래 TTL 안에 반영.

import { db } from '../firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';

export const SEASON_CONFIG_CHANGED = 'voda:seasonConfigChanged';

const TTL = 30 * 60 * 1000; // 시즌은 분 단위로 바뀔 일이 없다
let cache = { at: 0, val: null };
let inflight = null;

// 'MM-DD' 형식만 받는다 — 잘못된 값이 들어와도 기본값으로 떨어지게.
function isMMDD(v) {
  return typeof v === 'string' && /^\d{2}-\d{2}$/.test(v);
}

/** 동기 조회. 아직 안 받았으면 null → 호출부는 번들 기본값을 쓴다. */
export function getSeasonOverrides() {
  return cache.val;
}

/** 한 번만 받아서 캐시하고, 값이 바뀌었으면 이벤트로 알린다. */
export async function loadSeasonOverrides() {
  if (cache.val && Date.now() - cache.at < TTL) return cache.val;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const snap = await getDoc(doc(db, 'config', 'seasons'));
      const d = snap.exists() ? snap.data() : {};
      const out = { showAll: d.showAll === true, windows: {} };
      Object.entries(d).forEach(([key, v]) => {
        if (key === 'showAll' || !v || typeof v !== 'object') return;
        const w = {};
        if (isMMDD(v.from) && isMMDD(v.to)) { w.from = v.from; w.to = v.to; }
        if (v.enabled === false) w.enabled = false;
        if (Object.keys(w).length) out.windows[key] = w;
      });
      const changed = JSON.stringify(out) !== JSON.stringify(cache.val);
      cache = { at: Date.now(), val: out };
      if (changed) window.dispatchEvent(new CustomEvent(SEASON_CONFIG_CHANGED));
      return out;
    } catch (e) {
      // 규칙·네트워크 문제로 못 읽어도 화면이 멈추면 안 된다 — 기본값 유지.
      console.warn('season config load failed:', e?.message);
      cache = { at: Date.now(), val: { showAll: false, windows: {} } };
      return cache.val;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// 시즌 창을 Firestore 에서 한 번 받아 오고, 도착하면 목록을 다시 그리게 한다.
// 동기 함수(styleCatalog) 가 읽는 캐시라 값 자체는 모듈에 있고, 여기서는
// "바뀌었다" 신호만 받아 리렌더를 유발한다 (credits-service 의 게스트
// 크레딧 이벤트와 같은 방식).
export function useSeasonConfig() {
  const [, bump] = useState(0);
  useEffect(() => {
    loadSeasonOverrides();
    const onChange = () => bump(n => n + 1);
    window.addEventListener(SEASON_CONFIG_CHANGED, onChange);
    return () => window.removeEventListener(SEASON_CONFIG_CHANGED, onChange);
  }, []);
}
